import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Stack,
  Pagination,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  LocalHospital as LocalHospitalIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchConsultationHistory } from "../../features/consultations/consultationHistorySlice";
import ConsultationDetailsModal from "../../components/ConsultationDetailsModal";
import { format } from "date-fns";
import debounce from "lodash.debounce"; // ✅ Debounce search input for performance

const ConsultationHistory = () => {
  const dispatch = useDispatch();
  const { data, loading, error, count } = useSelector((state) => state.consultationHistory);

  // ✅ **State for Server-Side Filtering, Sorting & Pagination**
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterDoctor, setFilterDoctor] = useState("");
  const [sortOption, setSortOption] = useState("date");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  // ✅ **Fetch Data with Pagination & Filters**
  useEffect(() => {
    dispatch(fetchConsultationHistory({ page, pageSize, searchTerm, filterDoctor, sortOption }));
  }, [dispatch, page, pageSize, searchTerm, filterDoctor, sortOption]);

  // ✅ **Debounced Search Function for Better Performance**
  const debouncedSearch = useCallback(
    debounce((value) => setSearchTerm(value), 500),
    []
  );

  const handleSearchChange = (event) => debouncedSearch(event.target.value);
  const handleFilterChange = (event) => setFilterDoctor(event.target.value);
  const handleSortChange = (event) => setSortOption(event.target.value);
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleViewDetails = (consultation) => setSelectedConsultation(consultation);
  const handleCloseModal = () => setSelectedConsultation(null);

  const getDoctorName = (doctor) => doctor?.user?.name || "Unknown Doctor";
  const formatConsultationDate = (dateString) => (dateString ? format(new Date(dateString), "EEEE, MMM d, yyyy 'at' h:mm a") : "Unknown Date");

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 3 }}>
        🏥 Consultation History
      </Typography>

      {/* Filters & Search */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="🔍 Search by doctor, diagnosis, or date..."
            onChange={handleSearchChange}
            InputProps={{ startAdornment: <FilterListIcon sx={{ mr: 1 }} /> }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Doctor</InputLabel>
            <Select value={filterDoctor} onChange={handleFilterChange} label="Doctor">
              <MenuItem value="">All</MenuItem>
              {Array.from(new Set(data.map((c) => c.doctor.id))).map((doctorId, index) => (
                <MenuItem key={index} value={doctorId}>
                  {getDoctorName(data.find((c) => c.doctor.id === doctorId)?.doctor)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Sort By</InputLabel>
            <Select value={sortOption} onChange={handleSortChange} label="Sort By">
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="doctor">Doctor</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Consultation History List */}
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Fetching consultation history...
          </Typography>
        </Box>
      ) : (
        <Paper elevation={3} sx={{ padding: 2, borderRadius: 2 }}>
          <List>
            {data.map((consultation) => (
              <ListItem
                key={consultation.id}
                sx={{
                  marginBottom: 2,
                  borderBottom: "1px solid #ddd",
                  padding: 2,
                  "&:hover": { backgroundColor: "#f9f9f9" },
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={2} alignItems="center">
                      <LocalHospitalIcon sx={{ color: "#1976D2" }} />
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {getDoctorName(consultation.doctor)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 20, color: "gray" }} />
                        <Typography variant="body2" sx={{ color: "gray" }}>
                          {formatConsultationDate(consultation.consultation_date)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: "gray", mt: 1 }}>
                        Diagnosis: {consultation.diagnosis || "No diagnosis available"}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {consultation.is_completed ? (
                          <Chip label="Completed" color="success" />
                        ) : (
                          <Chip label="Pending" color="warning" />
                        )}
                        {consultation.follow_up_required && <Chip label="Follow-up Needed" color="info" />}
                      </Stack>
                    </>
                  }
                />
                <IconButton color="primary" onClick={() => handleViewDetails(consultation)}>
                  <VisibilityIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
          {/* Server-Side Pagination */}
          <Pagination
            count={Math.ceil(count / pageSize)}
            page={page}
            onChange={handlePageChange}
            sx={{ display: "flex", justifyContent: "center", mt: 2 }}
          />
        </Paper>
      )}

      {/* Consultation Details Modal */}
      <ConsultationDetailsModal
        open={Boolean(selectedConsultation)}
        handleClose={handleCloseModal}
        consultationData={selectedConsultation || {}}
      />
    </Box>
  );
};

export default ConsultationHistory;
